<template>
  <div>
    <Table
      :columns="columns"
      :data="planSales"
      :is-row-clickable="true"
      @row-click="redirectToDetails"
      noDataMsg="Aucun contrat trouvé"
    >
      <!-- Contract information -->
      <template #contract="{ row }">
        <div class="flex flex-col">
          <span>
            <span class="font-semibold">{{ row.title }}</span>
            à
            <span class="font-medium">{{ formatPrice(row.price) }}{{ byMonth(row) }}</span>
          </span>
          <span class="font-light">{{ row.category }}</span>
          <span class="font-light">{{ row.id }} - {{ row.purchaseDate }}</span>
          <span v-if="row.orderReferenceId" class="font-light italic">
            Numéro de commande : {{ row.orderReferenceId }}
          </span>
          <span v-if="isCancelled(row)" class="font-light italic">Résilié le {{ row.cancellation?.date }}</span>
          <FieldSpan
            v-if="row.compliant === true"
            :value="t('compliant')"
            style-to-add="text-green-500 bg-green-100 border border-green-300 self-start"
          />
          <FieldSpan
            v-if="row.compliant === false"
            :value="t('non_compliant')"
            style-to-add="text-red-500 bg-red-100 border border-red-300 self-start"
          />
        </div>
      </template>

      <!-- Product information -->
      <template #product="{ row }">
        <div v-if="row.products?.length > 0" class="min-w-64 max-w-md whitespace-normal">
          <span class="font-semibold">{{ row.products[0].title }}</span>
          <div class="flex">
            <span class="font-light italic mr-2">{{ formatPrice(row.products[0].price) }}</span>
          </div>
        </div>
        <span v-else>-</span>
        <span v-if="isAdminRoute()">{{ row.store?.salesChannel }}</span>
      </template>

      <!-- Customer information -->
      <template #customer="{ row }">
        <div class="flex flex-col">
          <span class="font-semibold">{{ row.customer.firstName }} {{ row.customer.lastName }}</span>
          <span>{{ row.customer.email }}</span>
          <FieldSpan
            v-if="row.customer.emailFilledAutomatically"
            :value="t('email_auto')"
            style-to-add="text-red-500 bg-red-100 border border-red-300 self-start"
          />
        </div>
      </template>

      <!-- Status information -->
      <template #status="{ row }">
        <FieldSpan :value="row.status" :style-to-add="getStatusStyle(row)" />
      </template>
    </Table>

    <Pagination
      :currentPage="currentPage"
      :totalPages="totalPages"
      :totalCount="totalCount"
      @changePage="handleChangePage"
    />
  </div>
</template>

<script>
import { Pagination, Table, FieldSpan, formatPrice } from "@estaly/ui";
import { isAdminRoute } from "@estaly/ui/src/utils/route";
import { mapGetters } from "vuex";
import { useI18n } from "vue-i18n";

export default {
  name: "PlanSaleList",

  components: {
    Pagination,
    Table,
    FieldSpan,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: {
    planSales: {
      type: Array,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
    totalCount: {
      type: Number,
      required: true,
    },
    totalPages: {
      type: Number,
      required: true,
    },
  },

  computed: {
    ...mapGetters("planSales", ["isCancelled", "isExpired", "isSuspended", "statusListStyle"]),

    columns() {
      return [
        {
          label: "Contrat",
          field: "price",
          slot: "contract",
        },
        {
          label: "Produit",
          field: "product",
          slot: "product",
        },
        {
          label: "Client",
          field: "customer",
          slot: "customer",
        },
        {
          label: "Statut",
          field: "status",
          slot: "status",
        },
      ];
    },
  },

  methods: {
    isAdminRoute,
    // Helper methods
    byMonth(planSale) {
      return planSale.monthlyBilling ? "/mois" : "";
    },

    getStatusStyle(planSale) {
      return this.statusListStyle(planSale);
    },

    // Event handlers
    handleChangePage(newPage) {
      this.$emit("updatePage", newPage);
    },

    redirectToDetails(planSale) {
      const currentRoute = this.$route.path;
      this.$router.push(`${currentRoute}/${planSale.friendlyId}`);
    },

    // External utility
    formatPrice,
  },
};
</script>

<i18n src="./PlanSaleList.i18n.json"></i18n>
